'use client';

import { useEffect, useState } from 'react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, XAxis, YAxis } from 'recharts';

export default function ScorePotentialChart({ selectedSubject, scoreImprovement }) {
  const [currentSubjectData, setCurrentSubjectData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [yAxisDomain, setYAxisDomain] = useState([0, 100]);

  useEffect(() => {
    const filteredData = scoreImprovement?.filter(
      (item) => item.subjectMeta._id.toString() === selectedSubject.toString(),
    );
    const subjectData = filteredData?.[0];
    setCurrentSubjectData(subjectData);

    if (subjectData) {
      const { actual, improvement } = subjectData;
      const computedChartData = [
        {
          name: 'Actual score',
          actualScore: Math.max(Number(actual.actualScore) || 0, 0),
          improvedScore: 0,
          realActualScore: actual.actualScore,
          realImprovedScore: 0,
        },
        ...improvement.map((item) => ({
          name: `${item.reductionPercentage}% less error`,
          actualScore: Math.max(Number(actual.actualScore) || 0, 0),
          improvedScore: Math.max(item.improvedScoreByErrorReduction - actual.actualScore, 0),
          realActualScore: actual.actualScore,
          realImprovedScore: item.improvedScoreByErrorReduction,
        })),
      ];

      setChartData(computedChartData);

      const max = Math.max(
        subjectData.actual.maxScore,
        ...computedChartData.map((d) => d.actualScore + d.improvedScore),
      );
      setYAxisDomain([0, Math.ceil(max * 1.1)]);
    } else {
      setChartData([]);
      setYAxisDomain([0, 110]);
    }
  }, [selectedSubject, scoreImprovement]);

  const CustomBarLabel = ({ x, y, width, value }) => {
    const actual = Math.round(value);
    const max = currentSubjectData?.actual?.maxScore || 0;
    if (!actual) return null;

    return (
      <g transform={`translate(${x + width / 2}, ${y - 8})`} textAnchor="middle">
        <text
          style={{
            fontWeight: 'bold',
            fontSize: '12px',
            fill: 'var(--q3-neutral-default)',
          }}
        >
          {actual}
          <tspan
            style={{
              fontWeight: 'normal',
              fontSize: '10px',
              fill: 'var(--q3-neutral-light)',
            }}
          >
            /{max}
          </tspan>
        </text>
      </g>
    );
  };

  if (!currentSubjectData) return null;

  const gradientId = `improvedScoreGradient-${currentSubjectData?.subjectMeta?._id}`;
  const baseColor = 'var(--q3-accent-normal)';

  // Calculate minimum width based on number of bars
  // Each bar needs space + margins, minimum 120px per bar for readability
  const minWidth = Math.max(chartData.length * 120, 400);

  return (
    <div className="h-[300px] border border-t-0 overflow-x-auto rounded-b-xl border-[var(--q3-stroke-light)] pt-4 pointer-events-none">
      <div style={{ minWidth: `${minWidth}px`, height: '100%' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barSize={100} margin={{ left: 10, right: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--q3-stroke-light)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
              stroke="var(--q3-stroke-light)"
              axisLine={{ stroke: 'var(--q3-stroke-light)' }}
              interval={0} // Show all labels
            />
            <YAxis
              domain={yAxisDomain}
              tick={{ fontSize: 12, fill: 'var(--q3-neutral-light)' }}
              width={50}
              stroke="var(--q3-stroke-light)"
              axisLine={{ stroke: 'var(--q3-stroke-light)' }}
            />
            <defs>
              <linearGradient id={gradientId} x1="0" y1="1" x2="0" y2="0">
                <stop offset="0%" stopColor={baseColor} stopOpacity={0.1} />
                <stop offset="100%" stopColor={baseColor} stopOpacity={0.6} />
              </linearGradient>
            </defs>
            <Bar dataKey="actualScore" stackId="a" fill={baseColor} />
            <Bar dataKey="improvedScore" stackId="a" fill={`url(#${gradientId})`} label={<CustomBarLabel />} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
